import { ApplicationType_Enum, LoanStatusType_Enum } from "src/types/enums";
import type { LoanApplication } from "src/types/loan-application";
import type { ApplicationProperties, BadgeProperties, FlagProperties } from "src/types/status";
import useDifferenceInDays from "../time/use-difference-in-days";
import { dealerIntroNumber } from "src/const/contact-numbers";
import { useProposalStore } from "src/stores/proposal-store";
import { LoanProposalStatus, RuleResultType } from "@cf247/pricing";



const noVehicleFlag = {
  title: "No Vehicle Supplied",
  subtext: `No vehicle was supplied with this application. Get started by creating a new quote.<br/>If you have any questions, please contact us on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>.`,
  icon: "mdi-information-outline",
  borderColour: "tw-border-amber-400",
  bgColour: "tw-bg-amber-100",
  bgColourLight: "tw-bg-amber-50",
  textColour: null,
  ctaText: 'New Quote',
  ctaAction: 'new-quote',
  visibleIn: ["overview"]
} as FlagProperties;

export default function applicationStatus(app: LoanApplication){
  const vehicleFailFlag = {
    title: "Vehicle Outside Criteria",
    subtext: `This vehicle is outside criteria for the customers approved lenders, please create a new quote on a different vehicle.<br/>If you have any questions, please contact us on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>.`,
    icon: "mdi-close-circle-outline",
    borderColour: "tw-border-rose-400",
    bgColour: "tw-bg-rose-100",
    bgColourLight: "tw-bg-rose-50",
    textColour: null,
    ctaText: 'New Quote',
    ctaAction: 'new-quote',
    visibleIn: app.initialisingQuotes ? [] : ["overview"]
  } as FlagProperties;
  //console.log("getting app state", new Date())
  if(app.isQuickQuote){
    return {
      type: ApplicationType_Enum["Quick Quote"],
      state: null,
      decision: null,
      flags: [
        {
          title: "Quick Quote",
          subtext: "Convert to a proposal for lender decisions",
          icon: "mdi-information-outline",
          borderColour: "tw-border-sky-400",
          bgColour: "tw-bg-sky-100",
          bgColourLight: "tw-bg-sky-50",
          textColour: null,
          ctaText: "Convert to proposal",
          ctaAction: "convert",
          visibleIn: ["overview"]
        } as FlagProperties
      ],
      enableQuoting: true,
      enableDecisions: false,
      enableDocuments: false,
      enableActivityFeed: false,
      quoteHeader: null,
      hasDealSheet: false
    } as ApplicationProperties;
  }

  if(app.state === LoanStatusType_Enum.Completed){
    return {
      type: app.applicationType,
      state: null,
      decision: {
        label: "Completed",
        bgColour: "tw-bg-emerald-400",
        bgColourLight: "tw-bg-emerald-100",
        textColour: "tw-text-emerald-800",
        icon: "mdi-check-circle-outline",
        loader: false
      },
      flags: [{
        title: "Completed",
        subtext: "We've sent the funds to your nominated bank account.",
        icon: "mdi-check-circle-outline",
        borderColour: "tw-border-emerald-500",
        bgColour: "tw-bg-emerald-100",
        bgColourLight: "tw-bg-emerald-50",
        textColour: null,
        ctaText: null,
        ctaAction: null,
        visibleIn: ["overview"]
      }],
      enableQuoting: true,
      enableDecisions: true,
      enableDocuments: true,
      enableActivityFeed: true,
      quoteHeader: "Approved",
      quoteColour: "tw-bg-emerald-50",
      quoteBorder: "tw-border-emerald-500",
      hasDealSheet: true
    } as ApplicationProperties;
  }

  if([
    LoanStatusType_Enum["NTU Lost To Competitor"],
    LoanStatusType_Enum["NTU No Contact"],
    LoanStatusType_Enum["NTU Other"],
    LoanStatusType_Enum["NTU Outside Criteria"],
    LoanStatusType_Enum["NTU Rate Too High"]].includes(app.state)){
      return {
        type: app.applicationType,
        state: null,
        decision: {
          label: LoanStatusType_Enum[app.state],
          bgColour: "tw-bg-rose-400",
          bgColourLight: "tw-bg-rose-100",
          textColour: "tw-text-rose-800",
          icon: "mdi-close-circle-outline",
          loader: false
        },
        flags: [{
          title: LoanStatusType_Enum[app.state],
          subtext: `Not taken up - If anything changes, please contact us on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>`,
          icon: "mdi-close-circle-outline",
          borderColour: "tw-border-rose-400",
          bgColour: "tw-bg-rose-100",
          bgColourLight: "tw-bg-rose-50",
          textColour: null,
          ctaText: null,
          ctaAction: null,
          visibleIn: ["overview"]
        }],
        enableQuoting: false,
        enableDecisions: true,
        enableDocuments: false,
        enableActivityFeed: true,
        hasDealSheet: false,
      } as ApplicationProperties;
  }
  if (app.state === LoanStatusType_Enum["NTU Manual"]){
    return {
      type: app.applicationType,
      state: null,
      decision: {
        label: LoanStatusType_Enum[app.state],
        bgColour: "tw-bg-rose-400",
        bgColourLight: "tw-bg-rose-100",
        textColour: "tw-text-rose-800",
        icon: "mdi-close-circle-outline",
        loader: false
      },
      flags: [{
        title: LoanStatusType_Enum[app.state],
        subtext: `You have manually flagged this app as NTU, if you wish to unflag please click "Remove NTU Flag"`,
        icon: "mdi-close-circle-outline",
        borderColour: "tw-border-rose-400",
        bgColour: "tw-bg-rose-100",
        bgColourLight: "tw-bg-rose-50",
        textColour: null,
        ctaText: 'Remove NTU Flag',
        ctaAction: 'unflag-ntu',
        visibleIn: ["overview"]
      }],
      enableQuoting: false,
      enableDecisions: true,
      enableDocuments: false,
      enableActivityFeed: true,
      hasDealSheet: false,
    } as ApplicationProperties;
  }
  const isFull = app.applicationType === ApplicationType_Enum["Full Proposal"] || app.applicationType === ApplicationType_Enum.Business ? true : false;// isFullProposal(app);
  const hasProposals = app.proposals && app.proposals.length > 0;

  const isAppLessThan3MinsOld = (new Date().getTime() - new Date(app.created).getTime()) < 180000;
  
  const isApproved = hasProposals && app.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus as LoanProposalStatus === LoanProposalStatus.Accepted) ? true : false;
  const isConditional = hasProposals && app.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus as LoanProposalStatus === LoanProposalStatus.ConditionallyAccepted && (!p.customerRuleResults || !p.customerRuleResults.length || !p.customerRuleResults.find(r => r.resultType === RuleResultType.FixedDecline))) ? true : false;
  const isProvisional = hasProposals && app.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus as LoanProposalStatus === LoanProposalStatus.ProvisionalAcceptance && (!p.customerRuleResults || !p.customerRuleResults.length || !p.customerRuleResults.find(r => r.resultType === RuleResultType.FixedDecline))) ? true : false;
  const isProcessing = hasProposals && app.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus as LoanProposalStatus === LoanProposalStatus.Processing) ? true : false;

  const customerFailed = hasProposals && app.proposals.find(p => p.customerRuleResults && p.customerRuleResults.find(r => r.resultType === RuleResultType.FixedDecline)) ? true : false;
  const isDeclined = hasProposals && app.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus as LoanProposalStatus === LoanProposalStatus.Declined) ? true : false;
  const isFailed = hasProposals && app.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus as LoanProposalStatus === LoanProposalStatus.FailedSendRules) ? true : false;
  const hasRef = app.brokerReference && app.brokerReference > 0;
  
  let decision: BadgeProperties | null = null;
  let flags: FlagProperties[] | null = null;
  let enableQuoting = true;
  let enableDecisions = true;
  let enableDocuments = true;

  let quoteHeader: string | null = null;
  let quoteColour: string | null = null;
  let quoteBorder: string | null = null;

  let hasDealSheet = false;

  const isDocsState = app.payoutPack && app.payoutPack.applicationInfo && app.payoutPack.applicationInfo.intranetLoanDealPreId ? true : false;
  const isAwaitingPayout = app.state === LoanStatusType_Enum["Awaiting Payout"] ? true : false;

  const quotes = useProposalStore().savedQuotes;

  const hasQuotes = quotes && quotes.length > 0 ? true : false;
  const hasVehicle = app.vehicle && app.vehicle.vrm && app.vehicle.make ? true : false;

  if(isDocsState){

    quoteHeader = "Approved";
    quoteBorder = "tw-border-emerald-400";
    quoteColour = "tw-bg-emerald-50";
    hasDealSheet = true;

    let allDocsUploaded = false;
    let docsComplete = false;
    
    // const c = app.payoutPack!.customerRequirements ? app.payoutPack!.customerRequirements.length && app.payoutPack!.customerRequirements.filter(r => !r.hasFiles).length ? true : false : true;
    // const cm = app.payoutPack!.customerMiscRequirements ? app.payoutPack!.customerMiscRequirements.length && app.payoutPack!.customerMiscRequirements.filter(r => r.hasFiles).length ? true : false : true;
    // const d = app.payoutPack!.dealerRequirements ? app.payoutPack!.dealerRequirements.length && app.payoutPack!.dealerRequirements.filter(r => !r.hasFiles).length ? true : false : true;
    // const dm = app.payoutPack!.dealerMiscRequirements ? app.payoutPack!.dealerMiscRequirements.length && app.payoutPack!.dealerMiscRequirements.filter(r => r.hasFiles).length ? true : false : true;
    // allDocsUploaded = !c && !cm && !d && !dm;

    const docsNeededCountCustomer = (app.payoutPack!.customerRequirements ? app.payoutPack!.customerRequirements.length : 0) + (app.payoutPack!.customerMiscRequirements ? app.payoutPack!.customerMiscRequirements.length : 0);
    const docsUploadedCountCustomer = (app.payoutPack!.customerRequirements ? app.payoutPack!.customerRequirements.filter(r => r.hasFiles).length : 0) + (app.payoutPack!.customerMiscRequirements ? app.payoutPack!.customerMiscRequirements.filter(r => r.hasFiles).length : 0);
    const docsNeededCountDealer = (app.payoutPack!.dealerRequirements ? app.payoutPack!.dealerRequirements.length : 0) + (app.payoutPack!.dealerMiscRequirements ? app.payoutPack!.dealerMiscRequirements.length : 0);
    const docsUploadedCountDealer = (app.payoutPack!.dealerRequirements ? app.payoutPack!.dealerRequirements.filter(r => r.hasFiles).length : 0) + (app.payoutPack!.dealerMiscRequirements ? app.payoutPack!.dealerMiscRequirements.filter(r => r.hasFiles).length : 0);

    allDocsUploaded = (docsNeededCountCustomer === docsUploadedCountCustomer) && (docsNeededCountDealer === docsUploadedCountDealer) ? true : false;
    
    const hasSnags = (app.payoutPack!.customerRequirements ? app.payoutPack!.customerRequirements.filter(r => r.hasSnags).length > 0 : false) ||
      (app.payoutPack!.customerMiscRequirements ? app.payoutPack!.customerMiscRequirements.filter(r => r.hasSnags).length > 0 : false) ||
      (app.payoutPack!.dealerRequirements ? app.payoutPack!.dealerRequirements.filter(r => r.hasSnags).length > 0 : false) ||
      (app.payoutPack!.dealerMiscRequirements ? app.payoutPack!.dealerMiscRequirements.filter(r => r.hasSnags).length > 0 : false) ? true : false;
    
    docsComplete = ((app.payoutPack!.customerRequirements.length === 0 || (app.payoutPack!.customerRequirements.filter(r => r.isVerified).length === app.payoutPack!.customerRequirements.length)) && 
      (app.payoutPack!.customerMiscRequirements.length === 0 || (app.payoutPack!.customerMiscRequirements.filter(r => r.isVerified).length === app.payoutPack!.customerMiscRequirements.length))) &&
      ((app.payoutPack!.dealerRequirements.length === 0 || (app.payoutPack!.dealerRequirements.filter(r => r.isVerified).length === app.payoutPack!.dealerRequirements.length)) && 
      (app.payoutPack!.dealerMiscRequirements.length === 0 || (app.payoutPack!.dealerMiscRequirements.filter(r => r.isVerified).length === app.payoutPack!.dealerMiscRequirements.length))) ? true : false;

    if(hasSnags){
      const snagCount = (app.payoutPack!.customerRequirements ? app.payoutPack!.customerRequirements.filter(r => r.hasSnags).length : 0) +
        (app.payoutPack!.customerMiscRequirements ? app.payoutPack!.customerMiscRequirements.filter(r => r.hasSnags).length : 0) +
        (app.payoutPack!.dealerRequirements ? app.payoutPack!.dealerRequirements.filter(r => r.hasSnags).length : 0) +
        (app.payoutPack!.dealerMiscRequirements ? app.payoutPack!.dealerMiscRequirements.filter(r => r.hasSnags).length : 0);
      flags = [{
        title: `${snagCount} Document${snagCount > 1 ? 's' : ''} Rejected`,
        subtext: `${snagCount > 1 ? (snagCount+' of the documents provided have') : 'A document has'} been rejected during verification. Please re-upload.<br/>If you have any questions, please contact us on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>`,
        icon: "mdi-close-circle-outline",
        borderColour: "tw-border-rose-400",
        bgColour: "tw-bg-rose-100",
        bgColourLight: "tw-bg-rose-50",
        textColour: null,
        ctaText: "Upload Documents",
        ctaAction: "upload-docs",
        visibleIn: ["overview"]
      }]
    }
    else if(!allDocsUploaded && !docsComplete){
      flags = [{
        title: "Documents Required",
        subtext: `We require some documents from you to finalise this application. ${docsUploadedCountCustomer + docsUploadedCountDealer} of ${docsNeededCountCustomer + docsNeededCountDealer} documents have been uploaded.<br/>If you have any questions, please contact us on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>`,
        icon: "mdi-information-outline",
        borderColour: "tw-border-amber-400",
        bgColour: "tw-bg-amber-100",
        bgColourLight: "tw-bg-amber-50",
        textColour: null,
        ctaText: "Upload Documents",
        ctaAction: "upload-docs",
        visibleIn: ["overview"]
      }]
    }
    else if(allDocsUploaded && !docsComplete){
      flags= [{
        title: "Documents Received",
        subtext: "We have received your documents and are currently busy reviewing them.",
        icon: "mdi-clock-outline",
        borderColour: "tw-border-sky-400",
        bgColour: "tw-bg-sky-100",
        bgColourLight: "tw-bg-sky-50",
        textColour: null,
        ctaText: null,
        ctaAction: null,
        visibleIn: ["overview"]
      }]
    }
    else if(docsComplete){
      flags = [{
        title: "Documents Complete",
        subtext: "All the documents you have provided have been verified by our team.",
        icon: "mdi-check-circle-outline",
        borderColour: "tw-border-emerald-500",
        bgColour: "tw-bg-emerald-100",
        bgColourLight: "tw-bg-emerald-50",
        textColour: null,
        ctaText: null,
        ctaAction: null,
        visibleIn: ["overview"]
      }]
    }
    return {
      type: app.applicationType,
      state: null,
      decision: {
        label: "Approved",
        shortLabel: "Approved",
        bgColour: "tw-bg-emerald-400",
        bgColourLight: "tw-bg-emerald-100",
        textColour: "tw-text-emerald-800",
        icon: "mdi-check-circle-outline",
        loader: false,
        id: 0
      },
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }

  if(useDifferenceInDays(app.created, new Date()) > 31 && !isDocsState){
    return {
      type: isFull ? app.applicationType : ApplicationType_Enum["Eligibility"],
      state: null,
      decision: {
        label: "Expired",
        bgColour: "tw-bg-amber-400",
        bgColourLight: "tw-bg-amber-100",
        textColour: "tw-text-amber-800",
        icon: "mdi-close-circle-outline",
        loader: false,
        id: 6
      },
      flags: [{
        title: "Expired",
        subtext: "This application and its decisions have expired.",
        icon: "mdi-close-circle-outline",
        borderColour: "tw-border-amber-400",
        bgColour: "tw-bg-amber-100",
        bgColourLight: "tw-bg-amber-50",
        textColour: null,
        ctaText: "Repropose",
        ctaAction: "repropose",
        visibleIn: ["overview", "quotes", "decisions", "documents"]
      }],
      enableQuoting: false,
      enableDecisions: false,
      enableDocuments: false,
      enableActivityFeed: true
    } as ApplicationProperties
  }

  if(!hasRef){
    decision = {
      label: "Awaiting Broker Reference",
      bgColour: "tw-bg-amber-400",
      bgColourLight: "tw-bg-amber-200",
      textColour: "tw-text-amber-800",
      icon: "mdi-clock-outline",
      loader: true
    }
  }

  if(isAppLessThan3MinsOld || !hasProposals || (isProcessing && !isApproved && !isProvisional && !isConditional)){
    flags = [{
      title: "Awaiting Decision",
      subtext: "We'll notify you once this customer has a decision.",
      icon: "mdi-clock-outline",
      borderColour: "tw-border-sky-400",
      bgColour: "tw-bg-sky-100",
      bgColourLight: "tw-bg-sky-50",
      textColour: null,
      ctaText: null,
      ctaAction: null,
      visibleIn: ["overview", "decisions", "quotes"],
    }];
    if(!isFull && hasRef){
      flags.unshift(
        {
          title: "Eligibility Check",
          subtext: "Convert to a full proposal to progress this application",
          icon: "mdi-information-outline",
          borderColour: "tw-border-sky-400",
          bgColour: "tw-bg-sky-100",
          bgColourLight: "tw-bg-sky-50",
          textColour: null,
          ctaText: "Convert to Full Proposal",
          ctaAction: "convert-full",
          visibleIn: ["overview", "quotes", "decisions", "customer", "documents"]
        }
      )
    }
    enableQuoting = false;
    enableDecisions = isProcessing ? true : false;
    decision =  decision ?? {
      label: "Awaiting Decision",
      bgColour: "tw-bg-amber-400",
      bgColourLight: "tw-bg-amber-100",
      textColour: "tw-text-amber-800",
      icon: "mdi-clock-outline",
      loader: false,
      id: 3
    }
    return {
      type: isFull ? app.applicationType : ApplicationType_Enum.Eligibility,
      state: null,
      decision,
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }
  else if(!isApproved && !isConditional && !isProvisional && !isFailed && !customerFailed && isDeclined){
    flags = [{
      title: "Declined",
      subtext: `This customer has been declined for finance. If you’re aware of any new information that could help us reassess the case, feel free to get in touch on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>.`,
      icon: "mdi-close-circle-outline",
      borderColour: "tw-border-rose-400",
      bgColour: "tw-bg-rose-100",
      bgColourLight: "tw-bg-rose-50",
      textColour: null,
      ctaText: null,
      ctaAction: null,
      visibleIn: ["overview", "quotes", "documents"]
    }];
    enableQuoting = false;
    enableDocuments = false;
    decision = {
      label: "Declined",
      bgColour: "tw-bg-rose-400",
      bgColourLight: "tw-bg-rose-100",
      textColour: "tw-text-rose-800",
      icon: "mdi-close-circle-outline",
      loader: false,
      id: 7
    }
    return {
      type: isFull ? app.applicationType : ApplicationType_Enum.Eligibility,
      state: null,
      decision,
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }
  else if(!isApproved && !isConditional && !isProvisional && (isFailed || customerFailed)){
    flags = [{
      title: "Customer Outside Lender Criteria",
      subtext: `This customer has fallen outside some of the lenders criteria for finance. If you think the customers circumstances will change, please contact us on <a href="tel:${dealerIntroNumber}" class="tw-text-slate-700">${dealerIntroNumber}</a>`,
      icon: "mdi-close-circle-outline",
      borderColour: "tw-border-amber-400",
      bgColour: "tw-bg-amber-100",
      bgColourLight: "tw-bg-amber-50",
      textColour: null,
      ctaText: null,
      ctaAction: null,
      visibleIn: ["overview", "customer", "decisions", "quotes", "documents"]
    }];
    enableQuoting = false;
    enableDocuments = false;
    decision = {
      label: "Customer Outside Criteria",
      bgColour: "tw-bg-amber-400",
      bgColourLight: "tw-bg-amber-100",
      textColour: "tw-text-amber-800",
      icon: "mdi-close-circle-outline",
      loader: false,
      id: 5
    }
    return {
      type: isFull ? app.applicationType : ApplicationType_Enum.Eligibility,
      state: null,
      decision,
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }
  else if(!isFull){
    quoteHeader = quoteHeader ?? "Predicted Approval"
    quoteBorder = quoteBorder ?? "tw-border-amber-400";
    quoteColour = quoteColour ?? "tw-bg-amber-50";
    flags = [{
      title: "Eligibility Check",
      subtext: "Convert to a full proposal to progress this application",
      icon: "mdi-information-outline",
      borderColour: "tw-border-sky-400",
      bgColour: "tw-bg-sky-100",
      bgColourLight: "tw-bg-sky-50",
      textColour: null,
      ctaText: "Convert to Full Proposal",
      ctaAction: "convert-full",
      visibleIn: ["overview", "quotes", "decisions", "customer", "documents"]
    } as FlagProperties]
    if(!isApproved && !isProvisional && isConditional){
      flags.push({
        title: "Conditional Approval",
        subtext: "This customer has been conditionally approved.",
        icon: "mdi-check-circle-outline",
        borderColour: "tw-border-emerald-500",
        bgColour: "tw-bg-emerald-100",
        bgColourLight: "tw-bg-emerald-50",
        textColour: null,
        ctaText: null,
        ctaAction: null,
        visibleIn: ["overview"]
      } as FlagProperties)
      decision = {
        label: "Conditionally Approved",
        bgColour: "tw-bg-emerald-400",
        bgColourLight: "tw-bg-emerald-100",
        textColour: "tw-text-emerald-800",
        icon: "mdi-check-circle-outline",
        loader: false,
        id: 2
      }
    }
    else if(!isApproved && isProvisional){
      if(!hasQuotes){
        flags.push({
          title: "Predicted Approval",
          subtext: "This customer has been approved in principal using our predictive model.",
          icon: "mdi-check-circle-outline",
          borderColour: "tw-border-amber-500",
          bgColour: "tw-bg-amber-100",
          bgColourLight: "tw-bg-amber-50",
          textColour: null,
          ctaText: null,
          ctaAction: null,
          visibleIn: ["overview"]
        } as FlagProperties)
        flags.push(hasVehicle ? vehicleFailFlag : noVehicleFlag)
      }
      decision = {
        label: "Predicted Approval",
        bgColour: "tw-bg-amber-400",
        bgColourLight: "tw-bg-amber-100",
        textColour: "tw-text-amber-800",
        icon: "mdi-check-circle-outline",
        loader: false, 
        id: 1
      }
    }
    else if(isApproved){
      if(!hasQuotes){
        flags.push({
          title: "Predicted Approval",
          subtext: "This customer has been approved in principal using our predictive model.",
          icon: "mdi-check-circle-outline",
          borderColour: "tw-border-amber-500",
          bgColour: "tw-bg-amber-100",
          bgColourLight: "tw-bg-amber-50",
          textColour: null,
          ctaText: null,
          ctaAction: null,
          visibleIn: ["overview"]
        } as FlagProperties)
        flags.push(hasVehicle ? vehicleFailFlag : noVehicleFlag)
      }
      decision = {
        label: "Predicted Approval",
        bgColour: "tw-bg-amber-400",
        bgColourLight: "tw-bg-amber-100",
        textColour: "tw-text-amber-800",
        icon: "mdi-check-circle-outline",
        loader: false,
        id: 1
      }
    }
  }
  else if(!isApproved && !isProvisional && isConditional){
    quoteHeader = quoteHeader ?? "Predicted Approval";
    quoteBorder = quoteBorder ?? "tw-border-amber-400";
    quoteColour = quoteColour ?? "tw-bg-amber-50";
    flags = flags ?? !hasQuotes ? [{
      title: "Conditional Approval",
      subtext: "This customer has been conditionally approved.",
      icon: "mdi-check-circle-outline",
      borderColour: "tw-border-amber-500",
      bgColour: "tw-bg-amber-100",
      bgColourLight: "tw-bg-amber-50",
      textColour: null,
      ctaText: null,
      ctaAction: null,
      visibleIn: ["overview"]
    }, 
    hasVehicle ? vehicleFailFlag : noVehicleFlag] : [];
    decision = {
      label: isDocsState || isAwaitingPayout ? "Approved" : "Conditionally Approved",
      bgColour: "tw-bg-emerald-400",
      bgColourLight: "tw-bg-emerald-100",
      textColour: "tw-text-emerald-800",
      icon: "mdi-check-circle-outline",
      loader: false,
      id: isDocsState || isAwaitingPayout ? 0 : 2
    }
    return {
      type: isFull ? app.applicationType: ApplicationType_Enum.Eligibility,
      state: null,
      decision,
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }
  else if(!isApproved && isProvisional){
    quoteHeader = quoteHeader ?? "Predicted Approval"
    quoteBorder = quoteBorder ?? "tw-border-amber-400";
    quoteColour = quoteColour ?? "tw-bg-amber-50";
    flags = flags ?? !hasQuotes ? [{
      title: "Predicted Approval",
      subtext: "This customer has been approved in principal using our predictive model.",
      icon: "mdi-check-circle-outline",
      borderColour: "tw-border-amber-500",
      bgColour: "tw-bg-amber-100",
      bgColourLight: "tw-bg-amber-50",
      textColour: null,
      ctaText: null,
      ctaAction: null,
      visibleIn: ["overview"]
    },
    hasVehicle ? vehicleFailFlag : noVehicleFlag] : [];
    decision = {
      label: isDocsState || isAwaitingPayout ? "Approved" : "Predicted Approval",
      bgColour: isDocsState || isAwaitingPayout ? "tw-bg-emerald-400" : "tw-bg-amber-400",
      bgColourLight: isDocsState || isAwaitingPayout ? "tw-bg-emerald-100" : "tw-bg-amber-100",
      textColour: isDocsState || isAwaitingPayout ? "tw-terxt-emerald-800" : "tw-text-amber-800",
      icon: "mdi-check-circle-outline",
      loader: false,
      id: isDocsState || isAwaitingPayout ? 0 : 1
    }
    return {
      type: isFull ? app.applicationType : ApplicationType_Enum.Eligibility,
      state: null,
      decision,
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }
  else if(isApproved){
    quoteHeader = quoteHeader ?? "Predicted Approval";
    quoteBorder = quoteBorder ?? "tw-border-amber-400";
    quoteColour = quoteColour ?? "tw-bg-amber-50";
    flags = flags ?? !hasQuotes ? [{
      title: "Predicted Approval",
      subtext: "This customer has been approved in principal using our predictive model.",
      icon: "mdi-check-circle-outline",
      borderColour: "tw-border-amber-500",
      bgColour: "tw-bg-amber-100",
      bgColourLight: "tw-bg-amber-50",
      textColour: null,
      ctaText: null,
      ctaAction: null,
      visibleIn: ["overview"]
    },
    hasVehicle ? vehicleFailFlag : noVehicleFlag] : [];
    decision = {
      label: isDocsState || isAwaitingPayout ? "Approved" : "Predicted Approval",
      bgColour: isDocsState || isAwaitingPayout ? "tw-bg-emerald-400" : "tw-bg-amber-400",
      bgColourLight: isDocsState || isAwaitingPayout ? "tw-bg-emerald-100" : "tw-bg-amber-100",
      textColour: isDocsState || isAwaitingPayout ? "tw-text-emerald-800" : "tw-text-amber-800",
      icon: "mdi-check-circle-outline",
      loader: false,
      id: isDocsState || isAwaitingPayout ? 0 : 1
    }
  }

  if(isFull){
    return {
      type: app.applicationType,
      state: null,
      decision,
      flags,
      enableQuoting,
      enableDecisions,
      enableDocuments,
      enableActivityFeed: true,
      quoteHeader,
      quoteColour,
      quoteBorder,
      hasDealSheet,
    } as ApplicationProperties
  }

  return {
    type: ApplicationType_Enum["Eligibility"],
    state: null,
    decision,
    flags,
    enableQuoting,
    enableDecisions,
    enableDocuments,
    enableActivityFeed: true,
    quoteHeader,
    quoteColour,
    quoteBorder,
    hasDealSheet,
  }

}